/**
 * PriceCorridor — Gradient bar + circle dot + flag pill
 *
 * Shows asking price position relative to floor on a fading gradient bar.
 * Pill + stem anchored from bottom so stem always touches dot on bar.
 */

const TEAL = '#3DABA5';
const RED = '#DC2626';
const CHARCOAL = '#1A1A1A';
const FONT = "'Manrope', 'Inter', -apple-system, sans-serif";

function currency(n: number) {
  return '$' + Math.round(n).toLocaleString();
}

interface PriceCorridorProps {
  price: number;  // cents
  floor: number;  // cents
}

export function PriceCorridor({ price: priceCents, floor: floorCents }: PriceCorridorProps) {
  // Convert cents to dollars for display
  const price = priceCents / 100;
  const floor = floorCents / 100;

  const range = Math.max(price * 1.3, floor * 3.5);
  const floorPct = Math.max(12, (floor / range) * 100);
  const pricePct = Math.min(88, Math.max(floorPct + 6, (price / range) * 100));
  const isAbove = price >= floor;
  const accent = isAbove ? TEAL : RED;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16 }}>
        <span style={{
          fontSize: 11, fontWeight: 600, letterSpacing: '0.08em',
          color: '#94A3B8', textTransform: 'uppercase' as const, fontFamily: FONT,
        }}>
          Price Position
        </span>
      </div>

      {/* Flag + stem above, dot on bar */}
      <div style={{ position: 'relative', height: 62 }}>
        {/* Pill + stem — anchored from bottom to touch dot */}
        <div style={{
          position: 'absolute', left: `${pricePct}%`,
          bottom: 15,
          transform: 'translateX(-50%)',
          display: 'flex', flexDirection: 'column' as const, alignItems: 'center',
          zIndex: 2,
        }}>
          {/* Pill */}
          <div style={{
            backgroundColor: accent, color: 'white',
            fontSize: 13, fontWeight: 700, padding: '4px 12px', borderRadius: 20,
            whiteSpace: 'nowrap' as const, fontFamily: FONT,
            boxShadow: `0 2px 8px ${accent}35`,
          }}>
            {currency(price)}
          </div>
          {/* Stem — connects pill to dot below */}
          <div style={{ width: 1.5, height: 10, backgroundColor: accent }} />
        </div>

        {/* Bar track at bottom */}
        <div style={{ position: 'absolute', bottom: 4, left: 0, right: 0 }}>
          <div style={{
            height: 8, borderRadius: 4, position: 'relative',
            background: `linear-gradient(90deg, ${accent}30 0%, ${accent}20 60%, ${accent}08 75%, white 90%)`,
          }}>
            {/* Floor tick */}
            <div style={{
              position: 'absolute', left: `${floorPct}%`, top: -3, bottom: -3,
              width: 1.5, backgroundColor: '#CBD5E1', transform: 'translateX(-50%)',
            }} />
            {/* Price dot — on the bar */}
            <div style={{
              position: 'absolute', left: `${pricePct}%`, top: '50%',
              transform: 'translate(-50%, -50%)',
              width: 14, height: 14, borderRadius: '50%',
              backgroundColor: 'white',
              border: `2.5px solid ${accent}`,
              boxShadow: `0 0 0 3px ${accent}15`,
              zIndex: 3,
            }} />
          </div>
        </div>
      </div>

      {/* Floor + market labels */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            fontSize: 12, fontWeight: 600, color: '#64748B',
            textTransform: 'uppercase' as const, letterSpacing: '0.04em', fontFamily: FONT,
          }}>
            Your Floor
          </span>
          <span style={{ fontSize: 14, fontWeight: 700, color: CHARCOAL, fontFamily: FONT }}>
            {currency(floor)}
          </span>
        </div>
        <span style={{ fontSize: 11, color: '#B0B8C4', fontFamily: FONT }}>
          market thins out →
        </span>
      </div>

      {/* Footer */}
      <div style={{
        fontSize: 11, color: '#B0B8C4', marginTop: 16,
        textAlign: 'center' as const, paddingBottom: 4, fontFamily: FONT,
      }}>
        Floor is the minimum to cover costs.
      </div>
    </div>
  );
}
